import { lazy, Suspense } from 'react'

import { createFileRoute, redirect } from '@tanstack/react-router'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { ConnectionListSkeleton } from '@/features/sso/components/skeletons'
import { TenantSettingsSelect } from '@/features/tenant/components/tenant-settings-select'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { tenantSessionQueries } from '@/shared/auth/tenant-session'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import {
	SettingsCard,
	SettingsCardTitle,
} from '@/components/common/settings-card'

const AuthenticationPage = lazy(
	() => import('@/features/sso/components/authentication-page'),
)

export const Route = createFileRoute(
	'/_dashboard/settings/tenant-settings/authentication',
)({
	// Re-run the SSO guard on tenant switch: an in-page switch only changes
	// `search.t` (path/params stay), so without this dep the loader result is
	// reused and the redirect below never re-evaluates for the new tenant.
	loaderDeps: ({ search }) => ({ t: search.t }),

	beforeLoad: () => roleAuthGuard(ROLES.Admin),

	loader: async ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant()

		if (tenant) {
			const tenantDetails = await queryClient.ensureQueryData({
				...tenantSessionQueries.details(tenant),
				revalidateIfStale: true,
			})

			// If Tenant' SKU don't allow SSO, redirect to tenant settings
			if (!tenantDetails.sku_info.sso_allowed) {
				throw redirect({ to: '/settings/tenant-settings' })
			}
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Authentication | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: RouteSkeleton,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	return (
		<>
			<div className="flex flex-col items-start justify-start gap-2">
				<p className="text-muted-foreground">Authentication for:</p>
				<TenantSettingsSelect adminOnly ssoOnly />
			</div>

			<Suspense fallback={<AuthenticationPageSkeleton />}>
				<AuthenticationPage />
			</Suspense>
		</>
	)
}

function RouteSkeleton() {
	return (
		<>
			<div className="flex flex-col items-start justify-start gap-2">
				<p className="text-muted-foreground">Settings for:</p>
				<TenantSettingsSelect adminOnly ssoOnly />
			</div>
			<AuthenticationPageSkeleton />
		</>
	)
}

function AuthenticationPageSkeleton() {
	return (
		<SettingsCard>
			<SettingsCardTitle>SSO Connections</SettingsCardTitle>
			<ConnectionListSkeleton />
		</SettingsCard>
	)
}
